import { useRef, useState, type FormEvent } from "react";
import {
  Archive,
  ArrowDownToLine,
  ArrowUpRight,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  File as FileIcon,
  FileText,
  Folder,
  Image,
  Inbox,
  Mail,
  Package,
  Pencil,
  Plus,
  Search,
  Send,
  ShieldCheck,
  ShoppingBag,
  Star,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import {
  uid as id,
  formatMoney as money,
  invoiceTotal as totalInvoice,
  downloadFile,
  toCSV,
} from "../../../core/src";
import type {
  CalendarEvent,
  FileItem,
  Invoice,
  InvoiceLine,
  Member,
  Message,
  Order,
  Product,
} from "../../../core/src/types";
import type { PageProps } from "../types";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  IconButton,
  Modal,
  PageHeader,
  Panel,
} from "../components/ui";
import "../operations.css";
import { RingChart } from "../components/charts";
import { OrbitalArtwork } from "../components/OrbitalArtwork";

const day = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const today = () => day();
const readableDate = (date: string, short = false) =>
  date
    ? new Date(`${date.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", {
        month: short ? "short" : "long",
        day: "numeric",
        ...(short ? {} : { year: "numeric" }),
      })
    : "Not scheduled";
const initialColor = "#c4dfce";
const validEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
type StatusTone = "green" | "blue" | "amber" | "red" | "neutral" | "purple";
const statusTones: Record<string, StatusTone> = {
  active: "green",
  paid: "green",
  delivered: "green",
  sent: "blue",
  shipped: "blue",
  processing: "purple",
  pending: "amber",
  invited: "amber",
  overdue: "red",
  cancelled: "red",
  draft: "neutral",
  archived: "neutral",
};
const tone = (status: string): StatusTone => statusTones[status] ?? "neutral";
const statusName = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1);
const bytes = (size: number) =>
  size >= 1024 * 1024
    ? `${(size / (1024 * 1024)).toFixed(1)} MB`
    : size >= 1024
      ? `${(size / 1024).toFixed(1)} KB`
      : `${size} B`;

function SearchField({
  value,
  onChange,
  placeholder = "Search…",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="ops-search">
      <Search size={17} aria-hidden="true" />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </label>
  );
}

function Metric({
  label,
  value,
  footnote,
  icon,
}: {
  label: string;
  value: string | number;
  footnote: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="metric-card ops-metric">
      <div className="ops-metric-top">
        <span>{label}</span>
        <span className="ops-metric-icon">{icon}</span>
      </div>
      <strong>{value}</strong>
      <p>{footnote}</p>
    </div>
  );
}

function ConfirmDelete({
  title,
  description,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal open onClose={onClose} title={title} description={description}>
      <div className="form-actions">
        <Button variant="secondary" onClick={onClose}>
          Keep it
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          <Trash2 size={15} />
          Delete
        </Button>
      </div>
    </Modal>
  );
}

function exportRows(name: string, rows: (string | number)[][]) {
  downloadFile(
    name,
    toCSV(rows[0].map(String), rows.slice(1)),
    "text/csv;charset=utf-8",
  );
}

export function ProductsPage({ state, mutate, notify }: PageProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);
  const categories = Array.from(
    new Set(state.products.map((product) => product.category)),
  ).sort();
  const products = state.products.filter(
    (product) =>
      `${product.name} ${product.sku}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (category === "all" || product.category === category) &&
      (status === "all" || product.status === status),
  );
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    if (!editing.name.trim() || !editing.sku.trim() || !editing.category.trim())
      return notify("Name, SKU and category are required.", "error");
    if (
      state.products.some(
        (product) =>
          product.id !== editing.id &&
          product.sku.toLowerCase() === editing.sku.trim().toLowerCase(),
      )
    )
      return notify("A product with that SKU already exists.", "error");
    if (
      !Number.isFinite(editing.price) ||
      editing.price < 0 ||
      !Number.isInteger(editing.stock) ||
      editing.stock < 0 ||
      editing.stock > 1_000_000
    )
      return notify(
        "Use a valid price and a whole-number stock quantity.",
        "error",
      );
    const product = {
      ...editing,
      name: editing.name.trim(),
      sku: editing.sku.trim(),
      category: editing.category.trim(),
    };
    const exists = state.products.some((item) => item.id === product.id);
    if (
      !mutate((previous) => ({
        ...previous,
        products: exists
          ? previous.products.map((item) =>
              item.id === product.id ? product : item,
            )
          : [product, ...previous.products],
      }))
    )
      return;
    setEditing(null);
    notify(exists ? "Product updated." : "Product added to your catalog.");
  };
  return (
    <div className="page-stack ops-page">
      <PageHeader
        eyebrow="COMMERCE"
        title="Product catalog"
        description="A little organization. A lot of possibilities."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                exportRows("elyxir-products.csv", [
                  ["Name", "SKU", "Category", "Price", "Stock", "Status"],
                  ...products.map((product) => [
                    product.name,
                    product.sku,
                    product.category,
                    product.price,
                    product.stock,
                    product.status,
                  ]),
                ]);
                notify("Product catalog exported.");
              }}
            >
              <Download size={16} />
              Export
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                setEditing({
                  id: id(),
                  name: "",
                  sku: "",
                  category: "",
                  price: 0,
                  stock: 0,
                  status: "draft",
                  color: initialColor,
                })
              }
            >
              <Plus size={17} />
              Add product
            </Button>
          </>
        }
      />
      <div className="stat-grid">
        <Metric
          label="Total products"
          value={state.products.length}
          footnote="Across your entire catalog"
          icon={<Package size={19} />}
        />
        <Metric
          label="Active products"
          value={
            state.products.filter((product) => product.status === "active")
              .length
          }
          footnote="Ready for your storefront"
          icon={<ShoppingBag size={19} />}
        />
        <Metric
          label="Low stock"
          value={
            state.products.filter(
              (product) => product.stock > 0 && product.stock < 10,
            ).length
          }
          footnote="Fewer than 10 units remaining"
          icon={<Archive size={19} />}
        />
        <Metric
          label="Inventory value"
          value={money(
            state.products.reduce(
              (sum, product) => sum + product.price * product.stock,
              0,
            ),
            state.settings.currency,
          )}
          footnote="At current catalog prices"
          icon={<ArrowUpRight size={19} />}
        />
      </div>
      <Panel
        title="Your collection"
        description={`${products.length} products to explore`}
        action={
          <div className="filter-tabs">
            {["all", "active", "draft", "archived"].map((value) => (
              <button
                type="button"
                key={value}
                className={status === value ? "active" : ""}
                onClick={() => setStatus(value)}
              >
                {statusName(value)}
              </button>
            ))}
          </div>
        }
      >
        <div className="toolbar">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search products or SKU…"
          />
          <select
            className="select"
            aria-label="Product category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </div>
        {products.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Inventory</th>
                  <th>Status</th>
                  <th>
                    <span className="ops-sr">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="ops-product">
                        <div
                          className="ops-product-art"
                          style={{ background: product.color }}
                        >
                          <Package size={24} />
                        </div>
                        <div>
                          <button
                            className="ops-record-link"
                            onClick={() => setEditing({ ...product })}
                          >
                            {product.name}
                          </button>
                          <div className="record-subtitle">{product.sku}</div>
                        </div>
                      </div>
                    </td>
                    <td>{product.category}</td>
                    <td className="mono">
                      {money(product.price, state.settings.currency)}
                    </td>
                    <td>
                      <span
                        className={`ops-stock ${product.stock === 0 ? "out" : product.stock < 10 ? "low" : ""}`}
                      />
                      {product.stock === 0
                        ? "Out of stock"
                        : `${product.stock} in stock`}
                    </td>
                    <td>
                      <Badge tone={tone(product.status)}>
                        {statusName(product.status)}
                      </Badge>
                    </td>
                    <td>
                      <div className="ops-actions">
                        <IconButton
                          label={`Edit ${product.name}`}
                          onClick={() => setEditing({ ...product })}
                        >
                          <Pencil size={16} />
                        </IconButton>
                        <IconButton
                          label={`Delete ${product.name}`}
                          onClick={() => setDeleting(product)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<Package size={28} />}
            title="No products found"
            description="Try a different search or add your first product."
          />
        )}
      </Panel>
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={
          state.products.some((product) => product.id === editing?.id)
            ? "Edit product"
            : "Add a product"
        }
        description="Give your next great product a place in the collection."
      >
        {editing && (
          <form onSubmit={save}>
            <div className="form-grid">
              <Field label="Product name">
                <input
                  className="input"
                  required
                  maxLength={100}
                  value={editing.name}
                  onChange={(event) =>
                    setEditing({ ...editing, name: event.target.value })
                  }
                />
              </Field>
              <Field label="SKU">
                <input
                  className="input"
                  required
                  maxLength={40}
                  value={editing.sku}
                  onChange={(event) =>
                    setEditing({ ...editing, sku: event.target.value })
                  }
                />
              </Field>
              <Field label="Category">
                <input
                  className="input"
                  list="product-categories"
                  required
                  maxLength={50}
                  value={editing.category}
                  onChange={(event) =>
                    setEditing({ ...editing, category: event.target.value })
                  }
                />
              </Field>
              <datalist id="product-categories">
                {categories.map((value) => (
                  <option key={value} value={value} />
                ))}
              </datalist>
              <Field label={`Price (${state.settings.currency})`}>
                <input
                  className="input"
                  type="number"
                  required
                  min="0"
                  max="999999999"
                  step="0.01"
                  value={editing.price}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      price: Number(event.target.value),
                    })
                  }
                />
              </Field>
              <Field label="Stock quantity">
                <input
                  className="input"
                  type="number"
                  required
                  min="0"
                  max="1000000"
                  step="1"
                  value={editing.stock}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      stock: Number(event.target.value),
                    })
                  }
                />
              </Field>
              <Field label="Status">
                <select
                  className="select"
                  value={editing.status}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      status: event.target.value as Product["status"],
                    })
                  }
                >
                  {["draft", "active", "archived"].map((value) => (
                    <option key={value} value={value}>
                      {statusName(value)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save product
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {deleting && (
        <ConfirmDelete
          title="Delete this product?"
          description={`${deleting.name} will be removed from your catalog. Existing order records are kept.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (
              !mutate((previous) => ({
                ...previous,
                products: previous.products.filter(
                  (product) => product.id !== deleting.id,
                ),
              }))
            )
              return;
            setDeleting(null);
            notify("Product deleted.");
          }}
        />
      )}
    </div>
  );
}

export function OrdersPage({ state, mutate, notify }: PageProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<string | null>(null);
  const orders = state.orders.filter(
    (order) =>
      `${order.id} ${order.customer} ${order.email}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (status === "all" || order.status === status),
  );
  const order = state.orders.find((item) => item.id === selected);
  const statuses: Order["status"][] = [
    "pending",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
  ];
  const changeStatus = (record: Order, nextStatus: Order["status"]) => {
    if (
      !mutate((previous) => ({
        ...previous,
        orders: previous.orders.map((item) =>
          item.id === record.id ? { ...item, status: nextStatus } : item,
        ),
      }))
    )
      return;
    notify(`Order ${record.id} marked ${nextStatus}.`);
  };
  return (
    <div className="page-stack ops-page">
      <PageHeader
        eyebrow="COMMERCE"
        title="Orders"
        description="Every order is the start of a good relationship."
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              exportRows("elyxir-orders.csv", [
                [
                  "Order",
                  "Customer",
                  "Email",
                  "Date",
                  "Total",
                  "Items",
                  "Status",
                ],
                ...orders.map((item) => [
                  item.id,
                  item.customer,
                  item.email,
                  item.date,
                  item.total,
                  item.items,
                  item.status,
                ]),
              ]);
              notify("Orders exported.");
            }}
          >
            <Download size={16} />
            Export orders
          </Button>
        }
      />
      <div className="stat-grid">
        <Metric
          label="Total orders"
          value={state.orders.length}
          footnote="All orders in this workspace"
          icon={<ShoppingBag size={19} />}
        />
        <Metric
          label="Order value"
          value={money(
            state.orders
              .filter((item) => item.status !== "cancelled")
              .reduce((sum, item) => sum + item.total, 0),
            state.settings.currency,
          )}
          footnote="Excluding cancelled orders"
          icon={<ArrowUpRight size={19} />}
        />
        <Metric
          label="To fulfill"
          value={
            state.orders.filter((item) =>
              ["pending", "processing"].includes(item.status),
            ).length
          }
          footnote="Awaiting your next move"
          icon={<Package size={19} />}
        />
        <Metric
          label="Delivered"
          value={
            state.orders.filter((item) => item.status === "delivered").length
          }
          footnote="Arrived at their destination"
          icon={<Check size={19} />}
        />
      </div>
      <Panel
        title="Order activity"
        description="Manage the details, from checkout to doorstep."
      >
        <div className="toolbar">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search orders or customers…"
          />
          <select
            aria-label="Filter order status"
            className="select"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="all">All statuses</option>
            {statuses.map((value) => (
              <option key={value} value={value}>
                {statusName(value)}
              </option>
            ))}
          </select>
        </div>
        {orders.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orders.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <button
                        className="ops-record-link mono"
                        onClick={() => setSelected(item.id)}
                      >
                        {item.id}
                      </button>
                    </td>
                    <td>
                      <div className="ops-person">
                        <Avatar name={item.customer} />
                        <div>
                          <div className="record-title">{item.customer}</div>
                          <div className="record-subtitle">{item.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{readableDate(item.date, true)}</td>
                    <td>{item.items}</td>
                    <td className="mono">
                      {money(item.total, state.settings.currency)}
                    </td>
                    <td>
                      <Badge tone={tone(item.status)}>
                        {statusName(item.status)}
                      </Badge>
                    </td>
                    <td>
                      <IconButton
                        label={`View order ${item.id}`}
                        onClick={() => setSelected(item.id)}
                      >
                        <ArrowUpRight size={17} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<ShoppingBag size={28} />}
            title="No orders to show"
            description="Orders matching your search will appear here."
          />
        )}
      </Panel>
      <Modal
        open={Boolean(order)}
        onClose={() => setSelected(null)}
        title={order ? `Order ${order.id}` : "Order details"}
        description="Manage this order’s fulfillment status."
      >
        {order && (
          <div className="stack">
            <div className="ops-detail-summary">
              <Avatar name={order.customer} size="lg" />
              <div>
                <h3>{order.customer}</h3>
                <p className="muted">{order.email}</p>
              </div>
              <Badge tone={tone(order.status)}>
                {statusName(order.status)}
              </Badge>
            </div>
            <dl className="ops-key-values">
              <div>
                <dt>Placed on</dt>
                <dd>{readableDate(order.date)}</dd>
              </div>
              <div>
                <dt>Items</dt>
                <dd>{order.items}</dd>
              </div>
              <div>
                <dt>Total</dt>
                <dd>{money(order.total, state.settings.currency)}</dd>
              </div>
            </dl>
            <Field label="Fulfillment status">
              <select
                className="select"
                value={order.status}
                onChange={(event) =>
                  changeStatus(order, event.target.value as Order["status"])
                }
              >
                {statuses.map((value) => (
                  <option key={value} value={value}>
                    {statusName(value)}
                  </option>
                ))}
              </select>
            </Field>
            <p className="ops-note">
              Demo order record. Status updates do not contact a carrier or
              charge a customer.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function InvoicesPage({ state, mutate, notify }: PageProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Invoice | null>(null);
  const invoice = state.invoices.find((item) => item.id === viewId);
  const invoices = state.invoices.filter(
    (item) =>
      `${item.number} ${item.customer}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (status === "all" || item.status === status),
  );
  const newInvoice = () => {
    const next =
      Math.max(
        1000,
        ...state.invoices.map(
          (item) => Number(item.number.match(/\d+$/)?.[0]) || 0,
        ),
      ) + 1;
    const due = new Date();
    due.setDate(due.getDate() + 30);
    setEditing({
      id: id(),
      number: `INV-${next}`,
      customer: "",
      email: "",
      date: today(),
      dueDate: day(due),
      status: "draft",
      lines: [{ description: "", quantity: 1, unitPrice: 0 }],
      tax: 0,
      notes: "",
    });
  };
  const editLine = (index: number, update: Partial<InvoiceLine>) =>
    editing &&
    setEditing({
      ...editing,
      lines: editing.lines.map((line, at) =>
        at === index ? { ...line, ...update } : line,
      ),
    });
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    if (
      !editing.number.trim() ||
      !editing.customer.trim() ||
      !editing.lines.length ||
      editing.lines.some(
        (line) =>
          !line.description.trim() ||
          !Number.isFinite(line.quantity) ||
          line.quantity <= 0 ||
          !Number.isFinite(line.unitPrice) ||
          line.unitPrice < 0,
      ) ||
      !Number.isFinite(editing.tax) ||
      editing.tax < 0 ||
      editing.tax > 100
    )
      return notify("Check the customer, line items and tax rate.", "error");
    if (!validEmail(editing.email))
      return notify("Enter a valid customer email address.", "error");
    if (editing.dueDate < editing.date)
      return notify(
        "The due date must be on or after the issue date.",
        "error",
      );
    if (
      state.invoices.some(
        (item) =>
          item.id !== editing.id &&
          item.number.toLowerCase() === editing.number.trim().toLowerCase(),
      )
    )
      return notify("That invoice number already exists.", "error");
    const saved = {
      ...editing,
      number: editing.number.trim(),
      customer: editing.customer.trim(),
      email: editing.email.trim(),
      lines: editing.lines.map((line) => ({
        ...line,
        description: line.description.trim(),
      })),
    };
    if (
      !mutate((previous) => ({
        ...previous,
        invoices: previous.invoices.some((item) => item.id === saved.id)
          ? previous.invoices.map((item) =>
              item.id === saved.id ? saved : item,
            )
          : [saved, ...previous.invoices],
      }))
    )
      return;
    setEditing(null);
    setViewId(saved.id);
    notify("Invoice saved.");
  };
  const printInvoice = (item: Invoice) => {
    const doc = window.open("", "_blank", "width=850,height=1000");
    if (!doc)
      return notify("Allow pop-ups to open the printable invoice.", "error");
    const escape = (value: string) =>
      value.replace(
        /[&<>"']/g,
        (char) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          })[char]!,
      );
    const subtotal = totalInvoice({ ...item, tax: 0 });
    const total = totalInvoice(item);
    const taxAmount = Math.round((total - subtotal) * 100) / 100;
    doc.document.write(
      `<!doctype html>
      <html lang="en"><head><meta charset="utf-8"><title>${escape(item.number)}</title>
      <style>
        body{font:14px/1.6 system-ui,sans-serif;color:#202820;padding:50px;max-width:900px;margin:auto}
        header{display:flex;justify-content:space-between;border-bottom:2px solid #202820;padding-bottom:24px}
        h1{margin:0;font-size:36px}h2{margin-top:30px}table{width:100%;border-collapse:collapse;margin:35px 0}
        th,td{text-align:left;padding:15px 0;border-bottom:1px solid #ddd}th:last-child,td:last-child{text-align:right}
        .total{text-align:right;font-size:20px}.muted{color:#667085}footer{margin-top:50px;white-space:pre-wrap}
        button{padding:10px 18px;margin-bottom:25px;cursor:pointer}@media print{button{display:none}body{padding:0}}
      </style></head><body>
      <button onclick="window.print()">Print / Save as PDF</button>
      <header><div><h1>${escape(state.settings.workspace)}</h1><span class="muted">Invoice</span></div>
      <div><b>${escape(item.number)}</b><br>Issued ${escape(readableDate(item.date))}<br>Due ${escape(readableDate(item.dueDate))}</div></header>
      <h2>Bill to</h2><b>${escape(item.customer)}</b><br>${escape(item.email)}
      <table><thead><tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Amount</th></tr></thead><tbody>
      ${item.lines.map((line) => `<tr><td>${escape(line.description)}</td><td>${line.quantity}</td><td>${escape(money(line.unitPrice, state.settings.currency))}</td><td>${escape(money(totalInvoice({ ...item, lines: [line], tax: 0 }), state.settings.currency))}</td></tr>`).join("")}
      </tbody></table><div class="total">Subtotal: ${escape(money(subtotal, state.settings.currency))}<br>
      Tax (${item.tax}%): ${escape(money(taxAmount, state.settings.currency))}<br><strong>Total: ${escape(money(total, state.settings.currency))}</strong></div>
      <footer>${escape(item.notes)}</footer><p class="muted">Status: ${escape(statusName(item.status))}.</p>
      </body></html>`,
    );
    doc.document.close();
    doc.opener = null;
  };
  const outstanding = state.invoices.filter((item) =>
    ["sent", "overdue"].includes(item.status),
  );
  const collected = state.invoices
    .filter((item) => item.status === "paid")
    .reduce((sum, item) => sum + totalInvoice(item), 0);
  const awaiting = outstanding.reduce(
    (sum, item) => sum + totalInvoice(item),
    0,
  );
  const overdue = outstanding
    .filter((item) => item.status === "overdue")
    .reduce((sum, item) => sum + totalInvoice(item), 0);
  const collectionPercent =
    collected + awaiting > 0
      ? Math.round((collected / (collected + awaiting)) * 100)
      : 0;
  return (
    <div className="page-stack ops-page invoices-page">
      <PageHeader
        eyebrow="FINANCE"
        title="Invoices"
        description="The complete picture. From great work to getting paid."
        actions={
          <Button variant="primary" onClick={newInvoice}>
            <Plus size={17} />
            Create invoice
          </Button>
        }
      />
      <div className="billing-overview">
        <section
          className="billing-balance"
          aria-label="Outstanding invoice balance"
        >
          <div className="billing-art">
            <OrbitalArtwork />
          </div>
          <div className="billing-topline">
            <span className="eyebrow">
              <span className="live-dot" /> OUTSTANDING BALANCE
            </span>
            <span className="billing-currency">{state.settings.currency}</span>
          </div>
          <strong className="billing-amount">
            {money(awaiting, state.settings.currency)}
          </strong>
          <p>
            {outstanding.length} invoices awaiting payment{" "}
            <ArrowUpRight size={15} />
          </p>
          <div className="billing-breakdown">
            <div>
              <span>
                <i />
                Awaiting payment
              </span>
              <strong>
                {money(awaiting - overdue, state.settings.currency)}
              </strong>
            </div>
            <div>
              <span>
                <i />
                Overdue
              </span>
              <strong>{money(overdue, state.settings.currency)}</strong>
            </div>
            <div>
              <span>Drafts</span>
              <strong>
                {state.invoices
                  .filter((item) => item.status === "draft")
                  .length.toString()
                  .padStart(2, "0")}
              </strong>
            </div>
          </div>
        </section>
        <section
          className="billing-collection panel"
          aria-label="Invoice collection overview"
        >
          <div className="collection-heading">
            <span className="metric-glyph">
              <Check size={19} />
            </span>
            <div>
              <h2>Collection overview</h2>
              <p>Invoice status, at a glance.</p>
            </div>
            <span className="collection-count">
              {state.invoices.length} invoices
            </span>
          </div>
          <div className="collection-content">
            <RingChart percent={collectionPercent} label="collected" />
            <div>
              <span className="collection-label">Total collected</span>
              <strong>{money(collected, state.settings.currency)}</strong>
              <p>Based on invoices marked as paid.</p>
              <span className="collection-note">
                <i /> Of issued invoice value
              </span>
            </div>
          </div>
          <div className="collection-footer">
            <span>
              Invoice value{" "}
              <strong>
                {money(
                  state.invoices.reduce(
                    (sum, item) => sum + totalInvoice(item),
                    0,
                  ),
                  state.settings.currency,
                )}
              </strong>
            </span>
            <span>Including drafts</span>
          </div>
        </section>
      </div>
      <Panel
        title="Your invoices"
        description="Keep every detail in good order."
      >
        <div className="toolbar">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search invoices or clients…"
          />
          <select
            className="select"
            aria-label="Invoice status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="all">All statuses</option>
            {["draft", "sent", "paid", "overdue"].map((value) => (
              <option key={value} value={value}>
                {statusName(value)}
              </option>
            ))}
          </select>
        </div>
        {invoices.length ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th>Issue date</th>
                  <th>Due date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <button
                        className="ops-record-link mono invoice-reference"
                        onClick={() => setViewId(item.id)}
                      >
                        <span className="invoice-file-icon">
                          <FileText size={17} />
                        </span>
                        {item.number}
                      </button>
                    </td>
                    <td>
                      <div className="record-title">{item.customer}</div>
                      <div className="record-subtitle">{item.email}</div>
                    </td>
                    <td>{readableDate(item.date, true)}</td>
                    <td>{readableDate(item.dueDate, true)}</td>
                    <td className="mono">
                      {money(totalInvoice(item), state.settings.currency)}
                    </td>
                    <td>
                      <Badge tone={tone(item.status)}>
                        {statusName(item.status)}
                      </Badge>
                    </td>
                    <td>
                      <IconButton
                        label={`View ${item.number}`}
                        onClick={() => setViewId(item.id)}
                      >
                        <ArrowUpRight size={16} />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={<FileText size={28} />}
            title="A clean slate"
            description="Create an invoice to start keeping track of your work."
            action={<Button onClick={newInvoice}>Create invoice</Button>}
          />
        )}
      </Panel>
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={
          state.invoices.some((item) => item.id === editing?.id)
            ? "Edit invoice"
            : "Create an invoice"
        }
        description="Line items and tax are calculated as you go."
        wide
      >
        {editing && (
          <form onSubmit={save}>
            <div className="form-grid">
              <Field label="Invoice number">
                <input
                  required
                  className="input"
                  maxLength={30}
                  value={editing.number}
                  onChange={(event) =>
                    setEditing({ ...editing, number: event.target.value })
                  }
                />
              </Field>
              <Field label="Customer / company">
                <input
                  required
                  className="input"
                  maxLength={120}
                  value={editing.customer}
                  onChange={(event) =>
                    setEditing({ ...editing, customer: event.target.value })
                  }
                />
              </Field>
              <Field label="Customer email">
                <input
                  required
                  type="email"
                  maxLength={254}
                  className="input"
                  value={editing.email}
                  onChange={(event) =>
                    setEditing({ ...editing, email: event.target.value })
                  }
                />
              </Field>
              <Field label="Status">
                <select
                  className="select"
                  value={editing.status}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      status: event.target.value as Invoice["status"],
                    })
                  }
                >
                  {["draft", "sent", "paid", "overdue"].map((value) => (
                    <option key={value} value={value}>
                      {statusName(value)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Issue date">
                <input
                  required
                  type="date"
                  className="input"
                  value={editing.date}
                  onChange={(event) =>
                    setEditing({ ...editing, date: event.target.value })
                  }
                />
              </Field>
              <Field label="Due date">
                <input
                  required
                  type="date"
                  className="input"
                  min={editing.date}
                  value={editing.dueDate}
                  onChange={(event) =>
                    setEditing({ ...editing, dueDate: event.target.value })
                  }
                />
              </Field>
            </div>
            <div className="ops-invoice-lines">
              <div className="ops-line-labels">
                <span>Description</span>
                <span>Quantity</span>
                <span>Unit price</span>
                <span />
              </div>
              {editing.lines.map((line, index) => (
                <div className="ops-invoice-line" key={index}>
                  <input
                    className="input"
                    required
                    aria-label={`Line ${index + 1} description`}
                    placeholder="Service or product"
                    maxLength={1000}
                    value={line.description}
                    onChange={(event) =>
                      editLine(index, { description: event.target.value })
                    }
                  />
                  <input
                    className="input"
                    required
                    aria-label={`Line ${index + 1} quantity`}
                    type="number"
                    min="0.01"
                    max="1000000"
                    step="0.01"
                    value={line.quantity}
                    onChange={(event) =>
                      editLine(index, { quantity: Number(event.target.value) })
                    }
                  />
                  <input
                    className="input"
                    required
                    aria-label={`Line ${index + 1} unit price`}
                    type="number"
                    min="0"
                    max="999999999"
                    step="0.01"
                    value={line.unitPrice}
                    onChange={(event) =>
                      editLine(index, { unitPrice: Number(event.target.value) })
                    }
                  />
                  <IconButton
                    label={`Remove line ${index + 1}`}
                    disabled={editing.lines.length === 1}
                    onClick={() =>
                      setEditing({
                        ...editing,
                        lines: editing.lines.filter((_, at) => at !== index),
                      })
                    }
                  >
                    <X size={16} />
                  </IconButton>
                </div>
              ))}
              <Button
                variant="ghost"
                type="button"
                disabled={editing.lines.length >= 200}
                onClick={() =>
                  setEditing({
                    ...editing,
                    lines: [
                      ...editing.lines,
                      { description: "", quantity: 1, unitPrice: 0 },
                    ],
                  })
                }
              >
                <Plus size={15} />
                Add line item
              </Button>
            </div>
            <div className="form-grid">
              <Field label="Tax rate (%)">
                <input
                  className="input"
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={editing.tax}
                  onChange={(event) =>
                    setEditing({ ...editing, tax: Number(event.target.value) })
                  }
                />
              </Field>
              <div className="ops-invoice-total">
                <span>Total including tax</span>
                <strong>
                  {money(totalInvoice(editing), state.settings.currency)}
                </strong>
              </div>
            </div>
            <Field label="Notes">
              <textarea
                className="input"
                rows={3}
                maxLength={2000}
                value={editing.notes}
                onChange={(event) =>
                  setEditing({ ...editing, notes: event.target.value })
                }
                placeholder="A personal note, payment details or terms…"
              />
            </Field>
            <p className="ops-note">
              Marking an invoice as sent updates the workspace record only. No
              email or payment is initiated.
            </p>
            <div className="form-actions">
              <Button
                variant="secondary"
                type="button"
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save invoice
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal
        open={Boolean(invoice) && !editing}
        onClose={() => setViewId(null)}
        title={invoice?.number || "Invoice"}
        description={invoice ? `For ${invoice.customer}` : ""}
        wide
      >
        {invoice && (
          <div className="stack">
            <div className="ops-detail-summary">
              <div className="ops-file-art">
                <FileText size={28} />
              </div>
              <div>
                <h3>{invoice.customer}</h3>
                <p className="muted">{invoice.email}</p>
              </div>
              <Badge tone={tone(invoice.status)}>
                {statusName(invoice.status)}
              </Badge>
            </div>
            <dl className="ops-key-values">
              <div>
                <dt>Issued</dt>
                <dd>{readableDate(invoice.date)}</dd>
              </div>
              <div>
                <dt>Due</dt>
                <dd>{readableDate(invoice.dueDate)}</dd>
              </div>
            </dl>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.description}</td>
                      <td>{line.quantity}</td>
                      <td>{money(line.unitPrice, state.settings.currency)}</td>
                      <td>
                        {money(
                          totalInvoice({ ...invoice, lines: [line], tax: 0 }),
                          state.settings.currency,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ops-invoice-total">
              <span>Total · {invoice.tax}% tax included</span>
              <strong>
                {money(totalInvoice(invoice), state.settings.currency)}
              </strong>
            </div>
            {invoice.notes && (
              <p className="ops-message-body">{invoice.notes}</p>
            )}
            <div className="form-actions ops-wrap">
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleting(invoice);
                  setViewId(null);
                }}
              >
                <Trash2 size={15} />
                Delete
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing({
                    ...invoice,
                    lines: invoice.lines.map((line) => ({ ...line })),
                  });
                }}
              >
                <Pencil size={15} />
                Edit
              </Button>
              <Button onClick={() => printInvoice(invoice)}>
                <Download size={15} />
                Print / PDF
              </Button>
            </div>
          </div>
        )}
      </Modal>
      {deleting && (
        <ConfirmDelete
          title="Delete this invoice?"
          description={`${deleting.number} will be removed from this workspace. This cannot be undone.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (
              !mutate((previous) => ({
                ...previous,
                invoices: previous.invoices.filter(
                  (item) => item.id !== deleting.id,
                ),
              }))
            )
              return;
            setDeleting(null);
            notify("Invoice deleted.");
          }}
        />
      )}
    </div>
  );
}

export function CalendarPage({ state, mutate, notify }: PageProps) {
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [deleting, setDeleting] = useState<CalendarEvent | null>(null);
  const [filter, setFilter] = useState("all");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const monday = state.settings.weekStartsMonday;
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (firstDay.getDay() + (monday ? 6 : 0)) % 7;
  const gridStart = new Date(firstDay);
  gridStart.setDate(1 - offset);
  const days = Array.from({ length: 42 }, (_, index) => {
    const value = new Date(gridStart);
    value.setDate(value.getDate() + index);
    return value;
  });
  const events = state.events.filter(
    (event) => filter === "all" || event.type === filter,
  );
  const upcoming = [...events]
    .filter((event) => event.date >= today())
    .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
    .slice(0, 5);
  const create = (date = today()) =>
    setEditing({
      id: id(),
      title: "",
      date,
      time: "10:00",
      duration: 60,
      type: "meeting",
      description: "",
    });
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    if (
      !editing.title.trim() ||
      !editing.date ||
      !editing.time ||
      editing.duration <= 0 ||
      editing.duration > 1440
    )
      return notify("Check the event title, date and duration.", "error");
    const value = { ...editing, title: editing.title.trim() };
    if (
      !mutate((previous) => ({
        ...previous,
        events: previous.events.some((item) => item.id === value.id)
          ? previous.events.map((item) => (item.id === value.id ? value : item))
          : [...previous.events, value],
      }))
    )
      return;
    setEditing(null);
    notify("Event saved to your calendar.");
  };
  const weekdayNames = monday
    ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="page-stack ops-page">
      <PageHeader
        eyebrow="WORKSPACE"
        title="Your calendar"
        description="Make room for what matters."
        actions={
          <Button variant="primary" onClick={() => create()}>
            <Plus size={17} />
            New event
          </Button>
        }
      />
      <div className="ops-calendar-layout">
        <Panel
          className="ops-calendar-panel"
          title={month.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
          description="A little space for your big ideas."
          action={
            <div className="ops-actions">
              <Button
                variant="secondary"
                onClick={() =>
                  setMonth(
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth(),
                      1,
                    ),
                  )
                }
              >
                Today
              </Button>
              <IconButton
                label="Previous month"
                onClick={() =>
                  setMonth(
                    new Date(month.getFullYear(), month.getMonth() - 1, 1),
                  )
                }
              >
                <ChevronLeft size={18} />
              </IconButton>
              <IconButton
                label="Next month"
                onClick={() =>
                  setMonth(
                    new Date(month.getFullYear(), month.getMonth() + 1, 1),
                  )
                }
              >
                <ChevronRight size={18} />
              </IconButton>
            </div>
          }
        >
          <div className="ops-calendar-scroll">
            <div className="ops-calendar">
              <div className="ops-weekdays">
                {weekdayNames.map((name) => (
                  <span key={name}>{name}</span>
                ))}
              </div>
              <div className="ops-calendar-days">
                {days.map((date) => {
                  const dateKey = day(date);
                  const daily = events
                    .filter((event) => event.date === dateKey)
                    .sort((a, b) => a.time.localeCompare(b.time));
                  return (
                    <div
                      key={dateKey}
                      className={`ops-day ${date.getMonth() !== month.getMonth() ? "outside" : ""} ${dateKey === today() ? "today" : ""}`}
                    >
                      <button
                        className="ops-day-number"
                        aria-label={`Create event on ${readableDate(dateKey)}`}
                        onClick={() => create(dateKey)}
                      >
                        {date.getDate()}
                      </button>
                      {daily.slice(0, 3).map((event) => (
                        <button
                          key={event.id}
                          className={`ops-calendar-event ${event.type}`}
                          title={`${event.time} · ${event.title}`}
                          onClick={() => setEditing({ ...event })}
                        >
                          <span>{event.time}</span>
                          {event.title}
                        </button>
                      ))}
                      {daily.length > 3 && (
                        <button
                          className="ops-more-events"
                          onClick={() => {
                            setSelectedDay(dateKey);
                          }}
                        >
                          +{daily.length - 3} more
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Panel>
        <div className="ops-calendar-aside">
          <Panel title="Calendars">
            <div className="ops-calendar-filters">
              {[
                { value: "all", label: "All events" },
                { value: "meeting", label: "Meetings" },
                { value: "deadline", label: "Deadlines" },
                { value: "personal", label: "Personal" },
              ].map((item) => (
                <button
                  key={item.value}
                  className={filter === item.value ? "active" : ""}
                  onClick={() => setFilter(item.value)}
                >
                  <span className={`ops-event-dot ${item.value}`} />
                  {item.label}
                  {filter === item.value && <Check size={15} />}
                </button>
              ))}
            </div>
          </Panel>
          <Panel title="Up next" description="A glance at what’s ahead.">
            {upcoming.length ? (
              <div className="ops-upcoming">
                {upcoming.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => setEditing({ ...event })}
                  >
                    <span className={`ops-event-dot ${event.type}`} />
                    <div>
                      <strong>{event.title}</strong>
                      <span>
                        {readableDate(event.date, true)} · {event.time}
                      </span>
                      <small>{event.duration} min</small>
                    </div>
                    <ArrowUpRight size={15} />
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<CalendarDays size={26} />}
                title="A little breathing room"
                description="No upcoming events in this calendar."
              />
            )}
          </Panel>
          <div className="ops-note">
            <Clock3 size={16} />
            Times use your local device timezone. Calendar changes are saved to
            your workspace.
          </div>
        </div>
      </div>
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={
          state.events.some((item) => item.id === editing?.id)
            ? "Event details"
            : "Make some time"
        }
        description="Give your next plan a place on the calendar."
      >
        {editing && (
          <form onSubmit={save}>
            <div className="form-grid">
              <Field label="Event title">
                <input
                  className="input"
                  required
                  maxLength={120}
                  value={editing.title}
                  onChange={(event) =>
                    setEditing({ ...editing, title: event.target.value })
                  }
                />
              </Field>
              <Field label="Calendar">
                <select
                  className="select"
                  value={editing.type}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      type: event.target.value as CalendarEvent["type"],
                    })
                  }
                >
                  {["meeting", "deadline", "personal"].map((value) => (
                    <option key={value} value={value}>
                      {statusName(value)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date">
                <input
                  className="input"
                  required
                  type="date"
                  value={editing.date}
                  onChange={(event) =>
                    setEditing({ ...editing, date: event.target.value })
                  }
                />
              </Field>
              <Field label="Start time">
                <input
                  className="input"
                  required
                  type="time"
                  value={editing.time}
                  onChange={(event) =>
                    setEditing({ ...editing, time: event.target.value })
                  }
                />
              </Field>
              <Field label="Duration (minutes)">
                <input
                  className="input"
                  required
                  type="number"
                  min="1"
                  max="1440"
                  step="1"
                  value={editing.duration}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      duration: Number(event.target.value),
                    })
                  }
                />
              </Field>
            </div>
            <Field label="Description">
              <textarea
                className="input"
                rows={3}
                maxLength={2000}
                value={editing.description}
                onChange={(event) =>
                  setEditing({ ...editing, description: event.target.value })
                }
              />
            </Field>
            <div className="form-actions">
              {state.events.some((item) => item.id === editing.id) && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setDeleting(editing);
                    setEditing(null);
                  }}
                >
                  <Trash2 size={15} />
                  Delete
                </Button>
              )}
              <Button
                variant="secondary"
                type="button"
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save event
              </Button>
            </div>
          </form>
        )}
      </Modal>
      <Modal
        open={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        title={selectedDay ? readableDate(selectedDay) : "Daily agenda"}
        description="Everything planned for this day."
      >
        <div className="ops-upcoming">
          {events
            .filter((event) => event.date === selectedDay)
            .sort((a, b) => a.time.localeCompare(b.time))
            .map((event) => (
              <button
                key={event.id}
                onClick={() => {
                  setSelectedDay(null);
                  setEditing({ ...event });
                }}
              >
                <span className={`ops-event-dot ${event.type}`} />
                <div>
                  <strong>{event.title}</strong>
                  <span>
                    {event.time} · {event.duration} min
                  </span>
                </div>
                <ArrowUpRight size={15} />
              </button>
            ))}
        </div>
      </Modal>
      {deleting && (
        <ConfirmDelete
          title="Delete this event?"
          description={`“${deleting.title}” will be removed from your calendar.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (
              !mutate((previous) => ({
                ...previous,
                events: previous.events.filter(
                  (item) => item.id !== deleting.id,
                ),
              }))
            )
              return;
            setDeleting(null);
            notify("Event deleted.");
          }}
        />
      )}
    </div>
  );
}

export function InboxPage({ state, mutate, notify }: PageProps) {
  const [query, setQuery] = useState("");
  const [folder, setFolder] = useState("inbox");
  const [selected, setSelected] = useState<string | null>(null);
  const [compose, setCompose] = useState<Message | null>(null);
  const [deleting, setDeleting] = useState<Message | null>(null);
  const messages = state.messages
    .filter(
      (message) =>
        (folder === "starred" ? message.starred : message.folder === folder) &&
        `${message.sender} ${message.email} ${message.subject} ${message.body}`
          .toLowerCase()
          .includes(query.toLowerCase()),
    )
    .sort((a, b) => b.date.localeCompare(a.date));
  const message = state.messages.find((item) => item.id === selected);
  const update = (messageId: string, changes: Partial<Message>) =>
    mutate((previous) => ({
      ...previous,
      messages: previous.messages.map((item) =>
        item.id === messageId ? { ...item, ...changes } : item,
      ),
    }));
  const open = (item: Message) => {
    setSelected(item.id);
    if (!item.read) update(item.id, { read: true });
  };
  const draft = (reply?: Message) =>
    setCompose({
      id: id(),
      sender: state.settings.name,
      email: reply?.email || "",
      subject: reply ? `Re: ${reply.subject.replace(/^Re: /, "")}` : "",
      body: "",
      date: new Date().toISOString(),
      read: true,
      starred: false,
      folder: "drafts",
    });
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!compose) return;
    if (!compose.subject.trim() || !compose.body.trim())
      return notify("Add a subject and message before saving.", "error");
    if (!validEmail(compose.email))
      return notify("Enter a valid recipient email address.", "error");
    const value = {
      ...compose,
      subject: compose.subject.trim(),
      email: compose.email.trim(),
      date: new Date().toISOString(),
      folder: "drafts" as const,
    };
    if (
      !mutate((previous) => ({
        ...previous,
        messages: previous.messages.some((item) => item.id === value.id)
          ? previous.messages.map((item) =>
              item.id === value.id ? value : item,
            )
          : [value, ...previous.messages],
      }))
    )
      return;
    setCompose(null);
    setFolder("drafts");
    setSelected(value.id);
    notify("Draft saved to your workspace. No email was sent.", "info");
  };
  return (
    <div className="page-stack ops-page">
      <PageHeader
        eyebrow="COMMUNICATION"
        title="Inbox"
        description="Good conversations start here."
        actions={
          <Button variant="primary" onClick={() => draft()}>
            <Plus size={17} />
            Compose
          </Button>
        }
      />
      <div className="ops-mail-demo">
        <Mail size={16} />
        <span>
          Demo mailbox · Messages and drafts stay in your workspace. Connect
          your email provider to send or receive mail.
        </span>
      </div>
      <div className={`ops-mail-layout ${message ? "has-message" : ""}`}>
        <aside className="ops-mail-folders" aria-label="Mailbox folders">
          {[
            { value: "inbox", label: "Inbox", icon: <Inbox size={18} /> },
            { value: "starred", label: "Starred", icon: <Star size={18} /> },
            { value: "sent", label: "Sent", icon: <Send size={18} /> },
            { value: "drafts", label: "Drafts", icon: <FileText size={18} /> },
            { value: "archive", label: "Archive", icon: <Archive size={18} /> },
          ].map((item) => (
            <button
              key={item.value}
              className={folder === item.value ? "active" : ""}
              onClick={() => {
                setFolder(item.value);
                setSelected(null);
              }}
            >
              {item.icon}
              {item.label}
              <span>
                {
                  state.messages.filter((mail) =>
                    item.value === "starred"
                      ? mail.starred
                      : mail.folder === item.value,
                  ).length
                }
              </span>
            </button>
          ))}
          <div className="ops-mail-label">
            A quieter workspace.
            <br />
            <span>One conversation at a time.</span>
          </div>
        </aside>
        <section className="ops-mail-list" aria-label="Messages">
          <div className="ops-mail-search">
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Search messages…"
            />
          </div>
          {messages.length ? (
            messages.map((item) => (
              <div
                key={item.id}
                className={`ops-message-row ${!item.read ? "unread" : ""} ${selected === item.id ? "selected" : ""}`}
              >
                <button
                  className="ops-message-select"
                  onClick={() => open(item)}
                >
                  <div className="ops-message-top">
                    <span>
                      {item.folder === "drafts"
                        ? `To: ${item.email}`
                        : item.sender}
                    </span>
                    <small>{readableDate(item.date, true)}</small>
                  </div>
                  <strong>{item.subject}</strong>
                  <p>{item.body.slice(0, 95)}</p>
                </button>
                <IconButton
                  label={`${item.starred ? "Unstar" : "Star"} ${item.subject}`}
                  onClick={() => update(item.id, { starred: !item.starred })}
                >
                  <Star
                    size={15}
                    className={item.starred ? "ops-starred" : ""}
                    fill={item.starred ? "currentColor" : "none"}
                  />
                </IconButton>
              </div>
            ))
          ) : (
            <EmptyState
              icon={<Inbox size={26} />}
              title="All clear"
              description="There are no matching messages in this folder."
            />
          )}
        </section>
        <section className="ops-mail-detail" aria-label="Message details">
          {message ? (
            <>
              <div className="ops-mail-detail-actions">
                <Button
                  className="ops-mail-back"
                  variant="ghost"
                  onClick={() => setSelected(null)}
                >
                  <ChevronLeft size={16} />
                  Messages
                </Button>
                <Badge tone={message.folder === "drafts" ? "amber" : "neutral"}>
                  {statusName(message.folder)}
                </Badge>
                <div className="ops-actions">
                  <IconButton
                    label={message.read ? "Mark unread" : "Mark read"}
                    onClick={() => {
                      if (!update(message.id, { read: !message.read })) return;
                      notify(
                        message.read ? "Marked as unread." : "Marked as read.",
                      );
                    }}
                  >
                    <Mail size={17} />
                  </IconButton>
                  {message.folder !== "archive" && (
                    <IconButton
                      label="Archive message"
                      onClick={() => {
                        if (!update(message.id, { folder: "archive" })) return;
                        setSelected(null);
                        notify("Message archived.");
                      }}
                    >
                      <Archive size={17} />
                    </IconButton>
                  )}
                  <IconButton
                    label="Delete message"
                    onClick={() => setDeleting(message)}
                  >
                    <Trash2 size={17} />
                  </IconButton>
                </div>
              </div>
              <h2>{message.subject}</h2>
              <div className="ops-person">
                <Avatar name={message.sender} />
                <div>
                  <div className="record-title">{message.sender}</div>
                  <div className="record-subtitle">
                    {message.folder === "drafts" ? "To: " : ""}
                    {message.email}
                  </div>
                </div>
                <time className="ops-mail-date">
                  {readableDate(message.date, true)}
                </time>
              </div>
              <div className="ops-message-body">{message.body}</div>
              <Button
                variant="secondary"
                onClick={() =>
                  message.folder === "drafts"
                    ? setCompose({ ...message })
                    : draft(message)
                }
              >
                <Pencil size={15} />
                {message.folder === "drafts" ? "Edit draft" : "Draft a reply"}
              </Button>
            </>
          ) : (
            <div className="ops-mail-welcome">
              <div>
                <Mail size={32} />
              </div>
              <h2>A space for your conversations</h2>
              <p>Select a message to read it, or start a fresh draft.</p>
            </div>
          )}
        </section>
      </div>
      <Modal
        open={Boolean(compose)}
        onClose={() => setCompose(null)}
        title="New message"
        description="Compose a workspace draft. Sending requires an email integration."
        wide
      >
        {compose && (
          <form onSubmit={save}>
            <div className="stack">
              <Field label="To">
                <input
                  className="input"
                  type="email"
                  required
                  value={compose.email}
                  maxLength={254}
                  onChange={(event) =>
                    setCompose({ ...compose, email: event.target.value })
                  }
                  placeholder="hello@example.com"
                />
              </Field>
              <Field label="Subject">
                <input
                  className="input"
                  required
                  maxLength={200}
                  value={compose.subject}
                  onChange={(event) =>
                    setCompose({ ...compose, subject: event.target.value })
                  }
                />
              </Field>
              <Field label="Message">
                <textarea
                  className="input ops-compose-body"
                  required
                  rows={10}
                  maxLength={20000}
                  value={compose.body}
                  onChange={(event) =>
                    setCompose({ ...compose, body: event.target.value })
                  }
                  placeholder="Write something thoughtful…"
                />
              </Field>
            </div>
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCompose(null)}
              >
                Discard changes
              </Button>
              <Button variant="primary" type="submit">
                <FileText size={15} />
                Save draft
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {deleting && (
        <ConfirmDelete
          title="Delete this message?"
          description={`“${deleting.subject}” will be permanently removed from this demo mailbox.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (
              !mutate((previous) => ({
                ...previous,
                messages: previous.messages.filter(
                  (item) => item.id !== deleting.id,
                ),
              }))
            )
              return;
            if (selected === deleting.id) setSelected(null);
            setDeleting(null);
            notify("Message deleted.");
          }}
        />
      )}
    </div>
  );
}

const FILE_LIMIT = 1_000_000;
export function FilesPage({ state, mutate, notify }: PageProps) {
  const [query, setQuery] = useState("");
  const [folder, setFolder] = useState("all");
  const [view, setView] = useState("all");
  const [renaming, setRenaming] = useState<FileItem | null>(null);
  const [deleting, setDeleting] = useState<FileItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const used = state.files.reduce(
    (sum, file) => sum + (file.content ? new Blob([file.content]).size : 0),
    0,
  );
  const folders = Array.from(
    new Set(state.files.map((file) => file.folder)),
  ).sort();
  const files = state.files.filter(
    (file) =>
      file.name.toLowerCase().includes(query.toLowerCase()) &&
      (folder === "all" || file.folder === folder) &&
      (view !== "starred" || file.starred),
  );
  const upload = async (selectedFiles: FileList | File[]) => {
    if (uploading) return;
    const candidates = Array.from(selectedFiles);
    if (!candidates.length) return;
    if (state.files.length + candidates.length > 500)
      return notify("This workspace supports up to 500 files.", "error");
    const estimated = candidates.reduce(
      (sum, file) => sum + Math.ceil((file.size * 4) / 3) + 128,
      0,
    );
    if (used + estimated > FILE_LIMIT)
      return notify(
        "Workspace file storage is limited to 1 MB including encoding. Choose smaller files or remove an existing file.",
        "error",
      );
    setUploading(true);
    try {
      const additions = await Promise.all(
        candidates.map(
          (file) =>
            new Promise<FileItem>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () =>
                resolve({
                  id: id(),
                  name: file.name,
                  size: file.size,
                  type: file.type || "application/octet-stream",
                  folder: folder === "all" ? "Uploads" : folder,
                  date: today(),
                  starred: false,
                  content: String(reader.result),
                });
              reader.onerror = () =>
                reject(new Error(`Could not read ${file.name}`));
              reader.readAsDataURL(file);
            }),
        ),
      );
      if (
        !mutate((previous) => ({
          ...previous,
          files: [...additions, ...previous.files],
        }))
      )
        return;
      notify(
        `${additions.length} file${additions.length === 1 ? "" : "s"} saved to your workspace.`,
      );
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "The files could not be read.",
        "error",
      );
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };
  const download = async (file: FileItem) => {
    if (file.content === undefined)
      return notify(
        "This sample record has no downloadable file. Upload a file to try downloading.",
        "info",
      );
    try {
      const blob = file.content.startsWith("data:")
        ? await (await fetch(file.content)).blob()
        : new Blob([file.content], { type: file.type || "text/plain" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.name;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify("File downloaded.");
    } catch {
      notify("This file could not be downloaded.", "error");
    }
  };
  const fileIcon = (file: FileItem) =>
    file.type.startsWith("image/") ? (
      <Image size={31} />
    ) : /pdf|text|json|csv/.test(file.type) ? (
      <FileText size={31} />
    ) : (
      <FileIcon size={31} />
    );
  const rename = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!renaming) return;
    if (!renaming.name.trim() || /[\\/]/.test(renaming.name))
      return notify("Use a filename without slashes.", "error");
    const value = {
      ...renaming,
      name: renaming.name.trim(),
      folder: renaming.folder.trim() || "Uploads",
    };
    if (
      !mutate((previous) => ({
        ...previous,
        files: previous.files.map((file) =>
          file.id === value.id ? value : file,
        ),
      }))
    )
      return;
    setRenaming(null);
    notify("File details updated.");
  };
  return (
    <div className="page-stack ops-page">
      <PageHeader
        eyebrow="WORKSPACE"
        title="Files & assets"
        description="A home for the things you’re creating."
        actions={
          <Button
            variant="primary"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            <Upload size={17} />
            {uploading ? "Uploading…" : "Upload files"}
          </Button>
        }
      />
      <input
        className="ops-sr"
        tabIndex={-1}
        ref={fileInput}
        type="file"
        multiple
        onChange={(event) =>
          event.target.files && void upload(event.target.files)
        }
      />
      <div className="ops-storage-banner">
        <div className="ops-storage-icon">
          <Folder size={24} />
        </div>
        <div>
          <h3>Your creative space</h3>
          <p>{state.files.length} files, right where you need them.</p>
        </div>
        <div className="ops-storage-meter">
          <div>
            <span>Workspace file content</span>
            <strong>{bytes(used)} / 1 MB</strong>
          </div>
          <div className="ops-progress">
            <span
              style={{ width: `${Math.min(100, (used / FILE_LIMIT) * 100)}%` }}
            />
          </div>
        </div>
      </div>
      <Panel
        title="Browse your files"
        description="Upload, organize, and keep ideas close at hand."
        action={
          <div className="filter-tabs">
            <button
              className={view === "all" ? "active" : ""}
              onClick={() => setView("all")}
            >
              All files
            </button>
            <button
              className={view === "starred" ? "active" : ""}
              onClick={() => setView("starred")}
            >
              <Star size={14} />
              Starred
            </button>
          </div>
        }
      >
        <div className="toolbar">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Find a file…"
          />
          <select
            className="select"
            aria-label="File folder"
            value={folder}
            onChange={(event) => setFolder(event.target.value)}
          >
            <option value="all">All folders</option>
            {folders.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </div>
        <div
          className={`ops-file-drop ${dropActive ? "dragging" : ""}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDropActive(true);
          }}
          onDragLeave={() => setDropActive(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDropActive(false);
            void upload(event.dataTransfer.files);
          }}
        >
          <div className="ops-files-grid">
            {files.map((file) => (
              <article className="ops-file-card" key={file.id}>
                <div className="ops-file-card-top">
                  <div
                    className={`ops-file-art ${file.type.startsWith("image/") ? "image" : ""}`}
                  >
                    {fileIcon(file)}
                  </div>
                  <IconButton
                    label={`${file.starred ? "Unstar" : "Star"} ${file.name}`}
                    onClick={() =>
                      mutate((previous) => ({
                        ...previous,
                        files: previous.files.map((item) =>
                          item.id === file.id
                            ? { ...item, starred: !item.starred }
                            : item,
                        ),
                      }))
                    }
                  >
                    <Star
                      size={16}
                      fill={file.starred ? "currentColor" : "none"}
                      className={file.starred ? "ops-starred" : ""}
                    />
                  </IconButton>
                </div>
                <h3 title={file.name}>{file.name}</h3>
                <p>
                  {file.folder} <span>·</span> {bytes(file.size)}
                </p>
                <div className="ops-file-card-bottom">
                  <span>{readableDate(file.date, true)}</span>
                  <div className="ops-actions">
                    <IconButton
                      label={`Download ${file.name}`}
                      onClick={() => void download(file)}
                    >
                      <ArrowDownToLine size={15} />
                    </IconButton>
                    <IconButton
                      label={`Rename ${file.name}`}
                      onClick={() => setRenaming({ ...file })}
                    >
                      <Pencil size={15} />
                    </IconButton>
                    <IconButton
                      label={`Delete ${file.name}`}
                      onClick={() => setDeleting(file)}
                    >
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {!files.length && (
            <EmptyState
              icon={<Folder size={30} />}
              title="Space for something new"
              description="Upload a file or change your filters to see more."
            />
          )}
          <button
            className="ops-upload-area"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            <Upload size={23} />
            <strong>
              {uploading
                ? "Reading your files…"
                : "Drop your files here, or browse"}
            </strong>
            <span>
              Stored in your workspace · 1 MB total including encoding
            </span>
          </button>
        </div>
      </Panel>
      <Modal
        open={Boolean(renaming)}
        onClose={() => setRenaming(null)}
        title="File details"
        description="Make this file easy to find."
      >
        {renaming && (
          <form onSubmit={rename}>
            <div className="stack">
              <Field label="File name">
                <input
                  className="input"
                  required
                  maxLength={200}
                  value={renaming.name}
                  onChange={(event) =>
                    setRenaming({ ...renaming, name: event.target.value })
                  }
                />
              </Field>
              <Field label="Folder">
                <input
                  className="input"
                  list="asset-folders"
                  required
                  maxLength={60}
                  value={renaming.folder}
                  onChange={(event) =>
                    setRenaming({ ...renaming, folder: event.target.value })
                  }
                />
              </Field>
              <datalist id="asset-folders">
                {folders.map((value) => (
                  <option key={value} value={value} />
                ))}
              </datalist>
            </div>
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setRenaming(null)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save changes
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {deleting && (
        <ConfirmDelete
          title="Delete this file?"
          description={`${deleting.name} and its stored content will be permanently deleted.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (
              !mutate((previous) => ({
                ...previous,
                files: previous.files.filter((file) => file.id !== deleting.id),
              }))
            )
              return;
            setDeleting(null);
            notify("File deleted from your workspace.");
          }}
        />
      )}
    </div>
  );
}

export function TeamPage({ state, mutate, notify }: PageProps) {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [editing, setEditing] = useState<Member | null>(null);
  const [deleting, setDeleting] = useState<Member | null>(null);
  const members = state.members.filter(
    (member) =>
      `${member.name} ${member.email}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (role === "all" || member.role === role),
  );
  const owners = state.members.filter(
    (member) => member.role === "owner",
  ).length;
  const activeOwners = state.members.filter(
    (member) => member.role === "owner" && member.status === "active",
  ).length;
  const protectedOwner = (member?: Member) =>
    Boolean(
      member?.role === "owner" &&
      (owners <= 1 || (member.status === "active" && activeOwners <= 1)),
    );
  const originalMember = state.members.find(
    (member) => member.id === editing?.id,
  );
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing) return;
    if (!editing.name.trim()) return notify("A name is required.", "error");
    if (!validEmail(editing.email))
      return notify("Enter a valid member email address.", "error");
    if (
      state.members.some(
        (member) =>
          member.id !== editing.id &&
          member.email.toLowerCase() === editing.email.trim().toLowerCase(),
      )
    )
      return notify("A member with this email already exists.", "error");
    const before = state.members.find((member) => member.id === editing.id);
    if (
      protectedOwner(before) &&
      (editing.role !== "owner" ||
        (before?.status === "active" && editing.status !== "active"))
    )
      return notify("Your workspace needs at least one active owner.", "error");
    const value = {
      ...editing,
      name: editing.name.trim(),
      email: editing.email.trim(),
    };
    if (
      !mutate((previous) => ({
        ...previous,
        members: previous.members.some((member) => member.id === value.id)
          ? previous.members.map((member) =>
              member.id === value.id ? value : member,
            )
          : [...previous.members, value],
      }))
    )
      return;
    setEditing(null);
    notify(
      before
        ? "Member updated."
        : "Member added to your workspace. No invitation email was sent.",
      before ? "success" : "info",
    );
  };
  return (
    <div className="page-stack ops-page">
      <PageHeader
        eyebrow="PEOPLE"
        title="Better together"
        description="The people behind your next big thing."
        actions={
          <Button
            variant="primary"
            onClick={() =>
              setEditing({
                id: id(),
                name: "",
                email: "",
                role: "member",
                status: "invited",
                color: initialColor,
              })
            }
          >
            <Plus size={17} />
            Add member
          </Button>
        }
      />
      <div className="ops-team-banner">
        <div>
          <span className="eyebrow">YOUR WORKSPACE, YOUR PEOPLE</span>
          <h2>
            Good things happen
            <br />
            with a great team.
          </h2>
          <p>
            {
              state.members.filter((member) => member.status === "active")
                .length
            }{" "}
            active members in {state.settings.workspace}.
          </p>
        </div>
        <div className="ops-team-avatars">
          {state.members.slice(0, 5).map((member) => (
            <Avatar
              key={member.id}
              name={member.name}
              color={member.color}
              size="lg"
            />
          ))}
          <span className="ops-team-plus">
            <Users size={23} />
          </span>
        </div>
      </div>
      <Panel
        title="Team members"
        description={`${state.members.length} people making things happen`}
        action={<Badge tone="neutral">Demo roles</Badge>}
      >
        <div className="toolbar">
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search team members…"
          />
          <select
            className="select"
            aria-label="Member role"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            <option value="all">All roles</option>
            {["owner", "admin", "member", "viewer"].map((value) => (
              <option key={value} value={value}>
                {statusName(value)}
              </option>
            ))}
          </select>
        </div>
        {members.length ? (
          <div className="ops-team-grid">
            {members.map((member) => (
              <article className="ops-member-card" key={member.id}>
                <div className="ops-member-top">
                  <Badge tone={tone(member.status)}>
                    {statusName(member.status)}
                  </Badge>
                  <div className="ops-actions">
                    <IconButton
                      label={`Edit ${member.name}`}
                      onClick={() => setEditing({ ...member })}
                    >
                      <Pencil size={15} />
                    </IconButton>
                    <IconButton
                      label={`Remove ${member.name}`}
                      disabled={protectedOwner(member)}
                      onClick={() => setDeleting(member)}
                    >
                      <Trash2 size={15} />
                    </IconButton>
                  </div>
                </div>
                <Avatar name={member.name} color={member.color} size="lg" />
                <h3>{member.name}</h3>
                <p>{member.email}</p>
                <div className="ops-member-role">
                  <ShieldCheck size={14} />
                  {statusName(member.role)}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Users size={28} />}
            title="No matching people"
            description="Try a different name or role."
          />
        )}
        <p className="ops-note">
          These role records demonstrate team management. Authentication,
          permission enforcement and invitation delivery require your backend
          integration.
        </p>
      </Panel>
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={
          state.members.some((member) => member.id === editing?.id)
            ? "Edit team member"
            : "Add someone great"
        }
        description="Keep your team’s details and roles organized."
      >
        {editing && (
          <form onSubmit={save}>
            <div className="form-grid">
              <Field label="Full name">
                <input
                  className="input"
                  required
                  maxLength={100}
                  value={editing.name}
                  onChange={(event) =>
                    setEditing({ ...editing, name: event.target.value })
                  }
                />
              </Field>
              <Field label="Email address">
                <input
                  className="input"
                  required
                  type="email"
                  maxLength={200}
                  value={editing.email}
                  onChange={(event) =>
                    setEditing({ ...editing, email: event.target.value })
                  }
                />
              </Field>
              <Field
                label="Workspace role"
                hint={
                  protectedOwner(originalMember)
                    ? "The last active owner must keep ownership."
                    : undefined
                }
              >
                <select
                  className="select"
                  value={editing.role}
                  disabled={protectedOwner(originalMember)}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      role: event.target.value as Member["role"],
                    })
                  }
                >
                  {["owner", "admin", "member", "viewer"].map((value) => (
                    <option key={value} value={value}>
                      {statusName(value)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  className="select"
                  value={editing.status}
                  disabled={
                    protectedOwner(originalMember) &&
                    originalMember?.status === "active"
                  }
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      status: event.target.value as Member["status"],
                    })
                  }
                >
                  <option value="active">Active</option>
                  <option value="invited">Invited (workspace record)</option>
                </select>
              </Field>
            </div>
            <p className="ops-note">
              Saving an invited member creates a workspace record. No email
              invitation is sent.
            </p>
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save member
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {deleting && (
        <ConfirmDelete
          title="Remove this member?"
          description={`${deleting.name} will be removed from this workspace.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => {
            if (protectedOwner(deleting)) {
              notify("The workspace needs at least one active owner.", "error");
              return;
            }
            if (
              !mutate((previous) => ({
                ...previous,
                members: previous.members.filter(
                  (member) => member.id !== deleting.id,
                ),
              }))
            )
              return;
            setDeleting(null);
            notify("Member removed.");
          }}
        />
      )}
    </div>
  );
}
